import React, { useState, useEffect } from "react";
import type { OrderControlsProps } from "./types";

const TICK_SIZE = 1;

export const OrderControls: React.FC<OrderControlsProps> = ({
  side,
  quantity,
  price,
  floorPrice,
  highlightPrice,
  disableSide,
  priceError,
  onSideChange,
  onQuantityChange,
  onPriceChange,
  priceInputRef,
}) => {
  const minPrice = Math.ceil(floorPrice) + 1;
  const maxPrice = 99;
  const [priceInput, setPriceInput] = useState<string>(price.toString());

  useEffect(() => {
    setPriceInput(price.toString());
  }, [price]);

  const incrementQty = () => {
    onQuantityChange(Math.min((quantity ?? 0) + 1, 100));
  };

  const decrementQty = () => {
    const newQty = Math.max((quantity ?? 0) - 1, 0);
    onQuantityChange(newQty || null);
  };

  const handleQtyWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      incrementQty();
    } else {
      decrementQty();
    }
  };

  const incrementPrice = () => {
    const newPrice = Math.min(Math.round(price) + TICK_SIZE, maxPrice);
    onPriceChange(newPrice);
    setPriceInput(newPrice.toString());
  };

  const decrementPrice = () => {
    const newPrice = Math.max(Math.round(price) - TICK_SIZE, minPrice);
    onPriceChange(newPrice);
    setPriceInput(newPrice.toString());
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "") {
      setPriceInput("");
      return;
    }
    if (!/^\d*$/.test(val)) return;
    setPriceInput(val);
    const parsed = parseInt(val, 10);
    if (!Number.isNaN(parsed)) {
      onPriceChange(parsed);
    }
  };

  const handlePriceBlur = () => {
    const parsed = parseInt(priceInput, 10);
    if (Number.isNaN(parsed) || priceInput === "") {
      setPriceInput(price.toString());
    } else {
      setPriceInput(parsed.toString());
      onPriceChange(parsed);
    }
  };

  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      incrementPrice();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      decrementPrice();
    }
  };

  const handlePriceDoubleClick = () => {
    priceInputRef.current?.select();
  };

  return (
    <div className="orderControlsNativeRow grid grid-cols-[minmax(0,1.18fr)_minmax(0,0.92fr)_minmax(0,0.92fr)] gap-2.5 md:gap-4">
      <div className="min-w-0">
        <label className="mb-2 block text-center font-display text-[16px] font-bold text-foreground-subtle">Side</label>
        <div className="flex h-[52px] rounded-xl bg-background-muted p-1">
          <button
            type="button"
            onClick={() => !disableSide && onSideChange("BUY")}
            disabled={disableSide}
            className={`min-w-0 flex-1 rounded-lg px-2 font-display text-[18px] font-bold uppercase transition-colors ${
              disableSide
                ? side === "BUY"
                  ? "cursor-not-allowed bg-success/50 text-white/70"
                  : "cursor-not-allowed text-foreground-faint"
                : side === "BUY"
                  ? "bg-success text-black"
                  : "text-foreground-muted hover:bg-background-raised"
            }`}
          >
            Buy
          </button>
          <button
            type="button"
            onClick={() => !disableSide && onSideChange("SELL")}
            disabled={disableSide}
            className={`min-w-0 flex-1 rounded-lg px-2 font-display text-[18px] font-bold uppercase transition-colors ${
              disableSide
                ? side === "SELL"
                  ? "cursor-not-allowed bg-danger/50 text-white/70"
                  : "cursor-not-allowed text-foreground-faint"
                : side === "SELL"
                  ? "bg-danger text-black"
                  : "text-foreground-muted hover:bg-background-raised"
            }`}
          >
            Sell
          </button>
        </div>
      </div>

      <div className="min-w-0">
        <label className="mb-2 block text-center font-display text-[16px] font-bold text-foreground-subtle">Qty</label>
        <div className="flex h-[52px] overflow-hidden rounded-xl border border-border bg-background-muted">
          <button
            type="button"
            onClick={decrementQty}
            disabled={quantity === null || quantity <= 0}
            className="grid w-9 shrink-0 place-items-center bg-background-surface text-2xl text-foreground transition-colors hover:bg-background-raised disabled:cursor-not-allowed disabled:opacity-50"
          >
            -
          </button>
          <input
            type="number"
            min="1"
            max="100"
            value={quantity ?? ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "") {
                onQuantityChange(null);
              } else {
                onQuantityChange(Math.max(1, Math.min(100, parseInt(val, 10) || 1)));
              }
            }}
            onWheel={handleQtyWheel}
            className="h-full min-w-0 flex-1 bg-transparent text-center font-mono text-[24px] font-semibold text-foreground [appearance:textfield] focus:outline-none focus:ring-1 focus:ring-amber-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <button
            type="button"
            onClick={incrementQty}
            disabled={(quantity ?? 0) >= 100}
            className="grid w-9 shrink-0 place-items-center bg-background-surface text-2xl text-foreground transition-colors hover:bg-background-raised disabled:cursor-not-allowed disabled:opacity-50"
          >
            +
          </button>
        </div>
      </div>

      <div className="min-w-0">
        <label className="mb-2 block text-center font-display text-[16px] font-bold text-foreground-subtle">Price</label>
        <div className="flex h-[52px] overflow-hidden rounded-xl border border-border bg-background-muted">
          <button
            type="button"
            onClick={decrementPrice}
            className="grid w-9 shrink-0 place-items-center bg-background-surface text-2xl text-foreground transition-colors hover:bg-background-raised"
          >
            -
          </button>
          <input
            ref={priceInputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={priceInput}
            maxLength={2}
            onChange={handlePriceChange}
            onBlur={handlePriceBlur}
            onKeyDown={handlePriceKeyDown}
            onDoubleClick={handlePriceDoubleClick}
            placeholder={String(minPrice)}
            className={`h-full min-w-0 flex-1 bg-transparent text-center font-mono text-[24px] font-semibold text-foreground transition-all focus:outline-none focus:ring-1 ${
              priceError
                ? "ring-1 ring-danger"
                : highlightPrice
                  ? "ring-1 ring-amber-500"
                  : "focus:ring-amber-500"
            }`}
          />
          <button
            type="button"
            onClick={incrementPrice}
            className="grid w-9 shrink-0 place-items-center bg-background-surface text-2xl text-foreground transition-colors hover:bg-background-raised"
          >
            +
          </button>
        </div>
        {priceError && <div className="mt-1 text-[11px] text-danger">{priceError}</div>}
      </div>
    </div>
  );
};
